import React from 'react';
import classNames from 'classnames';
import { AccountActions, AccountStore, Component } from '../client';
import { cast } from '../common';
import PhoneInput from '../components/ux/Inputs/PhoneInput';

const loginIdentifierTypes = {
   EMAIL: 'email',
   PHONE: 'phone'
};

class LogIn extends Component {
   constructor() {
      super();

      this.state = {
         loginIdentifierType: loginIdentifierTypes.EMAIL,
         loginStep: 'credentials',
         email: '',
         normalizedEmail: '',
         phoneInputValue: '',
         normalizedPhone: '',
         phoneIsValid: false,
         password: '',
         showPhoneErrors: true,
         selectedTenantNo: null,
         pendingLoginArgs: null
      };

      this.stores = [AccountStore];
      this.storeKeys = ['loader', 'alertMessage', 'showMessage', 'authUser', 'checkForInstructions', 'requiresTenantSelection', 'tenantOptions'];

      this.emailInputRef = React.createRef();
      this.phoneInputRef = React.createRef();
   }

   componentDidMount() {
      AccountActions.resetErrorSync();
   }

   componentDidUpdate(prevProps, prevState) {
      if (this.state.authUser != null) {
         this.props.history.push('/');
         return;
      }

      if (!prevState) {
         return;
      }

      if (!prevState.requiresTenantSelection && this.state.requiresTenantSelection && this.state.loginStep !== 'tenant') {
         this.setState({ loginStep: 'tenant' });
         return;
      }

      if (prevState.requiresTenantSelection && !this.state.requiresTenantSelection && this.state.loginStep !== 'credentials') {
         this.setState({ loginStep: 'credentials' });
      }
   }

   _onEmailChange = (e) => {
      const rawEmail = e.target.value;
      this.setState({ email: rawEmail, normalizedEmail: this.normalizeEmail(rawEmail) });
   };

   _onPasswordChange = (e) => {
      this.setState({ password: e.target.value });
   };

   _onPhoneChange = (_event, value, meta = {}) => {
      const normalizedPhone = meta.phone || '';
      const nextValue = meta.inputValue ?? value ?? '';

      this.setState({
         phoneInputValue: nextValue,
         normalizedPhone,
         phoneIsValid: Boolean(meta.isValid && normalizedPhone)
      });
   };

   _handleSubmit = (event) => {
      event.preventDefault();

      this._onSignIn();
   };

   _handleTenantSubmit = (event) => {
      event.preventDefault();
      this._onContinueTenantSelection();
   };

   _onSignIn = () => {
      const { loginIdentifierType, normalizedEmail, normalizedPhone, password } = this.state;
      const isPhoneLogin = loginIdentifierType === loginIdentifierTypes.PHONE;

      if (!this.canSubmit()) {
         this.setState({
            showMessage: true,
            alertMessage: isPhoneLogin ? 'We need a valid phone number and password' : 'We need a valid email and password'
         });
         return;
      }

      AccountActions.showAuthLoaderSync();

      const args = {
         email: isPhoneLogin ? null : normalizedEmail,
         phone: isPhoneLogin ? normalizedPhone : null,
         loginIdentifierType,
         password,
         ignoreErrorFeedback: true
      };

      this.setState({
         pendingLoginArgs: args,
         selectedTenantNo: null,
         loginStep: 'credentials'
      });

      AccountActions.loginUser(args);
   };

   _onSelectTenant = (event) => {
      const noTenant = parseInt(event.target.value, 10);
      this.setState({ selectedTenantNo: Number.isNaN(noTenant) ? null : noTenant });
   };

   _onContinueTenantSelection = () => {
      const { selectedTenantNo, pendingLoginArgs } = this.state;
      if (selectedTenantNo == null) {
         return;
      }

      if (!pendingLoginArgs) {
         this.setState({
            loginStep: 'credentials',
            showMessage: true,
            alertMessage: 'Please log in again before selecting an account.'
         });
         return;
      }

      AccountActions.showAuthLoaderSync();
      AccountActions.loginUser({
         ...pendingLoginArgs,
         noTenant: selectedTenantNo,
         ignoreErrorFeedback: true
      });
   };

   _onBackToCredentials = () => {
      this.setState({ loginStep: 'credentials' });
   };

   normalizeEmail(emailAddress) {
      return emailAddress ? emailAddress.trim().toLowerCase() : '';
   }

   verifyEmailAddress(emailAddress) {
      let valid = false;
      let p = { emailAddress: emailAddress };
      try {
         cast(p, { emailAddress: { email: true } });
         valid = true;
      } catch (e) {
         valid = false;
      }

      return { valid, emailAddress };
   }

   canSubmit() {
      const { password } = this.state;
      return this.isIdentifierValid() && Boolean(password);
   }

   isIdentifierValid() {
      const { loginIdentifierType, normalizedEmail, phoneIsValid } = this.state;
      if (loginIdentifierType === loginIdentifierTypes.PHONE) {
         return phoneIsValid;
      }

      return this.verifyEmailAddress(normalizedEmail).valid;
   }

   focusIdentifierField(type = this.state.loginIdentifierType) {
      if (type === loginIdentifierTypes.EMAIL && this.emailInputRef.current) {
         this.emailInputRef.current.focus();
         return;
      }

      if (type === loginIdentifierTypes.PHONE) {
         const phoneRef = this.phoneInputRef.current;
         if (phoneRef && typeof phoneRef.focus === 'function') {
            phoneRef.focus();
         } else if (phoneRef && typeof phoneRef.focusInput === 'function') {
            phoneRef.focusInput();
         } else if (typeof document !== 'undefined') {
            const phoneInputElement = document.querySelector('.LogIn .PhoneInputWrapper input[type="tel"]');
            phoneInputElement && phoneInputElement.focus();
         }
      }
   }

   _toggleIdentifierType = () => {
      const nextType =
         this.state.loginIdentifierType === loginIdentifierTypes.EMAIL ? loginIdentifierTypes.PHONE : loginIdentifierTypes.EMAIL;

      if (nextType === this.state.loginIdentifierType) {
         return;
      }

      AccountActions.resetErrorSync();

      this.setState(
         {
            loginIdentifierType: nextType,
            showPhoneErrors: nextType === loginIdentifierTypes.PHONE
         },
         () => {
            this.focusIdentifierField(nextType);
         }
      );
   };

   _prepareToggleIdentifier = () => {
      if (this.state.loginIdentifierType === loginIdentifierTypes.PHONE) {
         this.setState({ showPhoneErrors: false });
      }
   };

   _releaseToggleIdentifier = () => {
      if (this.state.loginIdentifierType === loginIdentifierTypes.PHONE) {
         this.setState({ showPhoneErrors: true });
      }
   };

   render() {
      const {
         loginIdentifierType,
         loginStep,
         email,
         phoneInputValue,
         password,
         showMessage,
         alertMessage,
         loader,
         checkForInstructions,
         showPhoneErrors,
         selectedTenantNo,
         requiresTenantSelection,
         tenantOptions
      } = this.state;

      const isEmailMode = loginIdentifierType === loginIdentifierTypes.EMAIL;
      const disabled = !this.canSubmit();
      const showTenantSelection = loginStep === 'tenant' && requiresTenantSelection === true;
      const canContinueTenantSelection = selectedTenantNo != null;

      const toggleLabel = isEmailMode ? 'Login with phone instead' : 'Login with email instead';
      const toggleAriaLabel = isEmailMode ? 'Switch to phone login' : 'Switch to email login';

      const baloonClasses = classNames('balloon', { loading: loader, errored: showMessage });

      return (
         <div className='LogIn layout-fill'>
            <div className='ops brand-wrapper'>
               <div id='balloon' className={baloonClasses}>
                  <div className='hide--when-loading txt--left'>
                     <h1>{showTenantSelection ? 'Select your account' : 'Log in to OpsPerfect'}</h1>
                     {checkForInstructions && (
                        <div className='flash txt--center push--bottom'>Check your email for reset instructions.</div>
                     )}
                     <div id='error-box' className='flash txt--center hide--unless-errored'>
                        <h2 id='error-msg' className='txt--red flush'>
                           {alertMessage}
                        </h2>
                     </div>

                     {!showTenantSelection && (
                        <form method='post' onSubmit={this._handleSubmit}>
                           <div className='field'>
                              {isEmailMode ? (
                                 <>
                                    <label className='field__label' htmlFor='email'>
                                       Email
                                    </label>
                                    <input
                                       type='email'
                                       name='email'
                                       id='email'
                                       ref={this.emailInputRef}
                                       className='input--text'
                                       placeholder='e.g. joe@yourco.com'
                                       autoFocus={isEmailMode}
                                       autoComplete='email'
                                       autoCorrect='off'
                                       autoCapitalize='off'
                                       value={email}
                                       onChange={this._onEmailChange}
                                    />
                                 </>
                              ) : (
                                 <>
                                    <label className='field__label' id='login-phone-label'>
                                       Phone
                                    </label>
                                    <PhoneInput
                                       aria-labelledby='login-phone-label'
                                       aria-label='Phone number'
                                       name='phone'
                                       value={phoneInputValue}
                                       controlRef={this.phoneInputRef}
                                       autoFocus={!isEmailMode}
                                       onChange={this._onPhoneChange}
                                       required
                                       showMessageOnError={false}
                                    />
                                 </>
                              )}
                           </div>

                           <div className='field'>
                              <label className='field__label' htmlFor='password'>
                                 Password
                              </label>
                              <input
                                 type='password'
                                 name='password'
                                 id='password'
                                 className='input--text'
                                 autoComplete='off'
                                 autoCorrect='off'
                                 autoCapitalize='off'
                                 value={password}
                                 onChange={this._onPasswordChange}
                              />
                           </div>

                           <input type='submit' id='btnLogin' name='commit' value='Log in' className='action_button primary' />

                           <div className='login-toggle'>
                              <div className='login-toggle__divider' role='presentation'></div>
                              <button
                                 type='button'
                                 className='login-toggle__link'
                                 onMouseDown={this._prepareToggleIdentifier}
                                 onMouseUp={this._releaseToggleIdentifier}
                                 onMouseLeave={this._releaseToggleIdentifier}
                                 onTouchStart={this._prepareToggleIdentifier}
                                 onTouchEnd={this._releaseToggleIdentifier}
                                 onTouchCancel={this._releaseToggleIdentifier}
                                 onClick={this._toggleIdentifierType}
                                 aria-label={toggleAriaLabel}>
                                 {toggleLabel}
                              </button>
                           </div>
                        </form>
                     )}

                     {showTenantSelection && (
                        <form method='post' onSubmit={this._handleTenantSubmit}>
                           <fieldset className='tenant-selection' aria-label='Tenant selection'>
                              {(tenantOptions || []).map((option) => {
                                 const inputId = `tenant-option-${option.noTenant}`;
                                 const checked = selectedTenantNo === option.noTenant;
                                 return (
                                    <label className='tenant-selection__option' key={option.noTenant} htmlFor={inputId}>
                                       <input
                                          id={inputId}
                                          type='radio'
                                          name='tenant-option'
                                          value={option.noTenant}
                                          checked={checked}
                                          onChange={this._onSelectTenant}
                                       />
                                       <span>{option.tenantName}</span>
                                    </label>
                                 );
                              })}
                           </fieldset>

                           <input
                              type='submit'
                              name='continue'
                              value='Continue'
                              className='action_button primary'
                              disabled={!canContinueTenantSelection}
                           />

                           <div className='login-toggle'>
                              <button type='button' className='login-toggle__link' onClick={this._onBackToCredentials}>
                                 &larr; Back
                              </button>
                           </div>
                        </form>
                     )}
                  </div>

                  <h1 className='hide--unless-loading'>Logging you in…</h1>
                  <div className='wrap hide--unless-loading'>
                     <div className='loader'></div>
                  </div>
               </div>
               <div className='footer'>{!showTenantSelection && isEmailMode && <a href='/#/forgot'>Forgot password?</a>}</div>
            </div>
         </div>
      );
   }
}

export default LogIn;
